import { EmailMessageDTO } from '../services/apiClient';
import { DrawerViewConstants } from '../constants/DrawerViewConstants';

export interface UseEmailHandlersParams<T> {
  setSelectedItem: (item: T) => void;
  setDrawerTitle: (title: string) => void;
  setDrawerView: (view: any) => void;
  setIsDrawerOpen: (isOpen: boolean) => void;
  sendEmail: (data: EmailMessageDTO) => Promise<any>;
  fetchItems: (page: number, size: number, filters: any) => Promise<void>;
  currentPage: number;
  pageSize: number;
  searchFilters: any;
}

export interface UseEmailHandlersReturn<T> {
  handleSendEmail: (item: T) => void;
  handleSendEmailSubmit: (data: EmailMessageDTO) => Promise<void>;
  handleViewAllEmails: (item: T) => void;
  handleViewRecordEmails: (item: T) => void;
}

/**
 * Custom hook for managing email-related handlers across list components.
 * Provides consistent email functionality (send, view all, view record) for any DTO type.
 * 
 * @template T - The DTO type (e.g., ActivityDTO, CourseDTO, ScoreDTO)
 * @param params - Configuration object containing state setters and API functions
 * @returns Object containing four email handler functions
 * 
 * @example
 * ```tsx
 * const {
 *   handleSendEmail,
 *   handleSendEmailSubmit,
 *   handleViewAllEmails,
 *   handleViewRecordEmails,
 * } = useEmailHandlers<ActivityDTO>({
 *   setSelectedItem: setSelectedActivity,
 *   setDrawerTitle,
 *   setDrawerView,
 *   setIsDrawerOpen,
 *   sendEmail,
 *   fetchItems,
 *   currentPage,
 *   pageSize,
 *   searchFilters,
 * });
 * ```
 */
export function useEmailHandlers<T>({
  setSelectedItem,
  setDrawerTitle,
  setDrawerView,
  setIsDrawerOpen,
  sendEmail,
  fetchItems,
  currentPage,
  pageSize,
  searchFilters,
}: UseEmailHandlersParams<T>): UseEmailHandlersReturn<T> {
  const handleSendEmail = (item: T) => {
    setSelectedItem(item);
    setDrawerTitle('Send a message');
    setDrawerView(DrawerViewConstants.SEND_EMAIL);
    setIsDrawerOpen(true);
  };

  const handleSendEmailSubmit = async (data: EmailMessageDTO) => {
    try {
      await sendEmail(data);
      setIsDrawerOpen(false);
      setSelectedItem({} as T);
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const handleViewAllEmails = (item: T) => {
    setSelectedItem(item);
    setDrawerTitle('Message All');
    setDrawerView(DrawerViewConstants.VIEW_ALL_EMAIL_LIST);
    setIsDrawerOpen(true);
  };

  const handleViewRecordEmails = (item: T) => {
    setSelectedItem(item);
    setDrawerTitle('Message Component');
    setDrawerView(DrawerViewConstants.VIEW_RECORD_EMAIL_LIST);
    setIsDrawerOpen(true);
  };

  return {
    handleSendEmail,
    handleSendEmailSubmit,
    handleViewAllEmails,
    handleViewRecordEmails,
  };
}
